/**
 * One case — a record (C14 §10.6 item 3, ADR-0033; Wave 15C). Its header says
 * where it is, whose it is and when it is due; its parts are tabs, one file
 * each: where it is and its tasks (`CaseStage`, `CaseTasks`), its documents
 * (`Documents/CaseDocuments`), its timeline and history (`CaseTimeline`), its
 * time (`CaseTime`), its call-backs (`CaseCallbacks`) and a change of plan or
 * service (`CaseChanges`). FR-E1…FR-E12.
 *
 * **Every rule on this screen is the server's answer, not a re-derivation.**
 * `can_move_here` per stage, `can_decide_review`, `frozen` — all of them come
 * back on the detail payload, using the engine's own rule, so a control greys
 * out for exactly the reason the API would refuse. A second copy of the rule in
 * TypeScript agrees today and disagrees in a year, and the one the firm sees
 * would be the wrong one (the shape Wave 2B set for the workflow validator).
 *
 * **Messages is the one primary action.** `POST /chat/conversations` is
 * find-or-open, and this button is the only way into a case's thread — the
 * Messages inbox lists threads and cannot start one (found by the Wave 4
 * close's walk). *Assign* is under *More*: shown to anyone who can update the
 * case, while the rule about who may actually assign is the server's, read
 * back by the dialog on `can_assign`.
 *
 * **_Refund…_ is under *More* too** *(C27, spec §10.19 item 7; UAT report #3)*.
 * FR-E13 says a refund is *raised on a case*; it is executed on the receipt
 * (Wave 5A, by the `refund:approve` role, behind step-up), so the entry opens
 * the client's receipts — `/billing/receipts?client_id=` — where the refund
 * action already is. It is offered on Billing's own answer, `can_refund` on the
 * billing vocabulary, read only by someone who holds `ledger:can_update` (the
 * verb that answer starts from), so it appears exactly where the receipt's
 * *Refund* button would.
 */

import { Button, type DropdownMenuItem } from '@fiveox/ui'
import { MessageSquare, Undo2, UserPlus } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'

import { RecordPage, type RecordTab } from '../../components/page'
import { CASE, DOCUMENT, INVOICE, LEDGER, TASK, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { billing } from '../../lib/api/billing'
import { chat } from '../../lib/api/chat'
import { type CaseDetail as Detail, caseLabel, cases, dueLabel, statusPair } from '../../lib/api/cases'
import { CaseDocumentsPanel } from '../Documents/CaseDocuments'
import { AssignDialog } from './AssignDialog'
import { CaseCallbacks } from './CaseCallbacks'
import { CaseChanges } from './CaseChanges'
import { CaseStage } from './CaseStage'
import { CaseTasks } from './CaseTasks'
import { CaseTime } from './CaseTime'
import { CaseTimeline } from './CaseTimeline'
import { HandoffButtons } from './HandoffButton'
import { DUE_TONE } from './Queues'

export function CaseDetail() {
  const { caseId = '' } = useParams()
  const { can } = useEvaluation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [detail, setDetail] = useState<Detail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(false)
  const [opening, setOpening] = useState(false)
  const [canRefund, setCanRefund] = useState(false)
  // Billing's own answer (`can_refund`), asked only of someone who could get a
  // yes: it starts from `ledger:can_update`, and the vocabulary is behind
  // `invoice:can_view`. A failed read offers nothing — the receipt still
  // decides, so the cost of a missing entry is a click through Billing.
  const mayAskRefund = can(LEDGER, 'can_update') && can(INVOICE, 'can_view')
  useEffect(() => {
    if (!mayAskRefund) {
      setCanRefund(false)
      return
    }
    let live = true
    void billing
      .vocabulary()
      .then((body) => live && setCanRefund(Boolean(body.data.can_refund)))
      .catch(() => live && setCanRefund(false))
    return () => {
      live = false
    }
  }, [mayAskRefund])

  const load = useCallback(async () => {
    const body = await cases.read(caseId)
    setDetail(body.data)
    setLoading(false)
  }, [caseId])

  useEffect(() => {
    setLoading(true)
    void load().catch((err) => {
      setError(
        err instanceof ApiError && err.status === 404
          ? 'That case is not one you can see.'
          : 'The case could not be loaded.',
      )
      setLoading(false)
    })
  }, [load])

  const openThread = async () => {
    if (!caseId) return
    setOpening(true)
    setError(null)
    try {
      // `api()` returns the envelope; the thread is `body.data`, as
      // `routes/Chat/Inbox.tsx` reads it.
      const body = await chat.open({ case_id: caseId })
      navigate(`/messages?thread=${body.data.id}`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That conversation could not be opened.')
    } finally {
      setOpening(false)
    }
  }

  const back = { to: '/cases', label: 'Cases' }

  if (loading || !detail) {
    return (
      <RecordPage
        back={back}
        title="Case"
        loading={loading || !error}
        error={error ?? (loading ? null : 'The case could not be loaded.')}
      />
    )
  }

  const canUpdate = can(CASE, 'can_update')
  const status = statusPair(detail.case)
  const due = dueLabel(detail.case.due_date)
  const openTasks = detail.tasks.filter((task) => !task.is_done).length
  const reload = async () => {
    await load().catch(() => setError('The case could not be reloaded.'))
  }

  const more: DropdownMenuItem[] = [
    ...(canUpdate
      ? [
          {
            id: 'assign',
            label: 'Assign',
            icon: <UserPlus size={14} aria-hidden />,
            description: 'Who does it, and who reviews it.',
            onSelect: () => setAssigning(true),
          },
        ]
      : []),
    ...(canRefund
      ? [
          {
            id: 'refund',
            label: 'Refund…',
            icon: <Undo2 size={14} aria-hidden />,
            description: 'Opens this client’s receipts; the refund is made on the receipt.',
            onSelect: () =>
              navigate(`/billing/receipts?client_id=${encodeURIComponent(detail.case.client_id)}`),
          },
        ]
      : []),
  ]

  const tabs: RecordTab[] = [
    {
      id: 'overview',
      label: 'Overview',
      badge: openTasks > 0 ? openTasks : undefined,
      content: (
        <div className="space-y-6">
          <CaseStage detail={detail} canUpdate={canUpdate} onChanged={reload} />
          <CaseTasks
            caseId={detail.case.id}
            tasks={detail.tasks}
            canUpdate={can(TASK, 'can_update')}
            canCreate={can(TASK, 'can_create')}
            onChanged={reload}
          />
        </div>
      ),
    },
    ...(can(DOCUMENT, 'can_view')
      ? [
          {
            id: 'documents',
            label: 'Documents',
            content: (
              <CaseDocumentsPanel caseId={detail.case.id} clientId={detail.case.client_id} />
            ),
          },
        ]
      : []),
    {
      id: 'timeline',
      label: 'Timeline',
      content: <CaseTimeline caseId={detail.case.id} history={detail.history} />,
    },
    {
      id: 'time',
      label: 'Time',
      content: <CaseTime caseId={detail.case.id} canUpdate={canUpdate} />,
    },
    {
      id: 'callbacks',
      label: 'Call-backs',
      content: <CaseCallbacks caseId={detail.case.id} canUpdate={canUpdate} />,
    },
    {
      id: 'changes',
      label: 'Plan and service',
      content: (
        <CaseChanges detail={detail} canUpdate={canUpdate} onChanged={() => void reload()} />
      ),
    },
  ]

  return (
    <>
      <RecordPage
        back={back}
        title={caseLabel(detail.case)}
        status={{ kind: 'case', status: detail.case.category, label: status.firm }}
        facts={[
          { label: 'Case', value: detail.case.number },
          {
            label: 'Client',
            value: (
              <Link to={`/clients/${detail.client.id}`} className="text-primary hover:underline">
                {detail.client.display_name}
              </Link>
            ),
          },
          { label: 'Service', value: detail.service.name },
          { label: 'Due', value: <span className={DUE_TONE[due.tone]}>{due.text}</span> },
          {
            label: 'With',
            // Eight characters of the user id. ProDesk has no member directory
            // to turn one into a name (ADR-0012 §2) — the ask is open against
            // the platform, and inventing a name here would be a name nothing
            // could check.
            value: detail.case.assignee_user_id
              ? detail.case.assignee_user_id.slice(0, 8)
              : 'Nobody yet',
          },
        ]}
        primary={
          canUpdate ? (
            <Button onClick={() => void openThread()} loading={opening} loadingText="Opening…">
              <MessageSquare size={16} aria-hidden /> Messages
            </Button>
          ) : undefined
        }
        more={more}
        error={error}
        tabs={tabs}
        tab={params.get('tab') ?? undefined}
        onTabChange={(id) => {
          const next = new URLSearchParams(params)
          if (id === 'overview') next.delete('tab')
          else next.set('tab', id)
          setParams(next, { replace: true })
        }}
      >
        {/* w6b — FR-E15's hand-off **[gated §9-7]**. Renders nothing in every
            environment today: no firm's integration state answers `active` for
            `ca-erp-itax`, because the broker is unbuilt. The absence is the
            feature — see `HandoffButton.tsx`. */}
        <HandoffButtons caseId={caseId} />
      </RecordPage>

      {assigning && (
        <AssignDialog
          detail={detail}
          onClose={() => setAssigning(false)}
          onAssigned={() => {
            setAssigning(false)
            void reload()
          }}
        />
      )}
    </>
  )
}
